import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Brain, 
  Users, 
  Eye, 
  Shield, 
  Scale, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  Sparkles,
  UserCheck,
  FileSearch,
  MessageSquare,
  Mail
} from 'lucide-react';

const AITransparency = () => {
  const lastUpdated = "20 January 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="h-8 w-8 text-[#42b1c2]" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Transparency</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Important Notice */}
          <Card className="mb-8 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    <strong>Important:</strong> This page explains how Luma Med Global uses artificial intelligence (AI) 
                    in our recruitment platform. We are committed to transparency about our AI-assisted tools and their 
                    limitations. This information is provided for transparency purposes and does not constitute technical 
                    or legal advice.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            
            {/* Our AI Philosophy */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Sparkles className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Our AI Philosophy
                </h2>
                <p className="text-gray-600 mb-4">
                  As a medically led recruitment platform, we believe that AI should augment and support human 
                  decision-making—not replace it. Our use of AI is guided by the following principles:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-[#42b1c2]/5 p-4 rounded-lg border border-[#42b1c2]/20">
                    <h3 className="font-medium text-gray-900 mb-2">Human-Centred</h3>
                    <p className="text-sm text-gray-600">
                      AI assists humans; it never replaces human judgement in critical decisions like hiring.
                    </p>
                  </div>
                  <div className="bg-[#42b1c2]/5 p-4 rounded-lg border border-[#42b1c2]/20">
                    <h3 className="font-medium text-gray-900 mb-2">Transparent</h3>
                    <p className="text-sm text-gray-600">
                      We clearly communicate when AI is being used and what its outputs mean.
                    </p>
                  </div>
                  <div className="bg-[#42b1c2]/5 p-4 rounded-lg border border-[#42b1c2]/20">
                    <h3 className="font-medium text-gray-900 mb-2">Fair</h3>
                    <p className="text-sm text-gray-600">
                      We actively work to identify and mitigate bias in our AI systems.
                    </p>
                  </div>
                  <div className="bg-[#42b1c2]/5 p-4 rounded-lg border border-[#42b1c2]/20">
                    <h3 className="font-medium text-gray-900 mb-2">Accountable</h3>
                    <p className="text-sm text-gray-600">
                      We take responsibility for our AI systems and their outcomes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How We Use AI */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Brain className="h-5 w-5 text-[#42b1c2] mr-2" />
                  How We Use AI
                </h2>
                <p className="text-gray-600 mb-4">
                  Luma Med Global uses AI-assisted tools to support the recruitment process in the following ways:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <FileSearch className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Profile & CV Screening Support</h3>
                        <p className="text-sm text-gray-600">
                          AI assists in organising and categorising candidate information from CVs and profiles. 
                          This helps streamline the initial review process but does not replace human evaluation 
                          of qualifications and suitability.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Candidate-Job Matching</h3>
                        <p className="text-sm text-gray-600">
                          AI algorithms help identify potential matches between candidate profiles and job requirements 
                          based on factors such as qualifications, experience, specialisation, and preferences. 
                          These matches are suggestions only—final decisions are made by employers.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <MessageSquare className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Video Interview Feedback (Simulated)</h3>
                        <p className="text-sm text-gray-600">
                          Our video interview feature currently provides <strong>simulated demonstration feedback</strong> only. 
                          This is clearly labelled and is intended to show candidates how the feature will work 
                          in future versions. Actual AI-powered analysis is not yet live.
                        </p>
                        <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                          <p className="text-xs text-amber-700">
                            <strong>Note:</strong> All interview feedback is currently marked as "Simulated / Demo – Not Live AI Analysis"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <Sparkles className="h-5 w-5 text-[#42b1c2] mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Workflow Efficiency</h3>
                        <p className="text-sm text-gray-600">
                          AI helps automate routine administrative tasks such as scheduling, notifications, 
                          and document organisation, allowing our team to focus on providing personalised support.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What AI Does NOT Do */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  What AI Does NOT Do
                </h2>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 font-medium">
                    AI does not make final hiring decisions. All employment decisions are made by human recruiters 
                    and employers.
                  </p>
                </div>
                
                <p className="text-gray-600 mb-4">
                  To be clear about our AI limitations:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span><strong>No automated rejection:</strong> AI does not automatically reject candidates from consideration</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span><strong>No scoring or ranking:</strong> AI does not assign pass/fail scores or rank candidates for employers</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span><strong>No credential verification:</strong> AI does not verify professional licences or certifications</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span><strong>No clinical judgement:</strong> AI does not assess clinical competence or make medical judgements</span>
                  </li>
                  <li className="flex items-start">
                    <XCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-1" />
                    <span><strong>No replacement for interviews:</strong> AI outputs do not replace proper interviews conducted by employers</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Human Oversight */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <UserCheck className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Human Oversight
                </h2>
                <p className="text-gray-600 mb-4">
                  Human oversight is a fundamental part of our AI implementation:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Review by professionals:</strong> Our recruitment team, led by medical professionals, 
                      reviews AI-generated suggestions and can override them at any point.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Employer control:</strong> Employers receive AI-assisted information as recommendations 
                      only and make all final decisions independently.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Candidate appeal:</strong> Candidates can contact us if they believe an AI-assisted 
                      process has been unfair, and we will review their case with human judgement.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Regular audits:</strong> We periodically review AI outputs for accuracy and fairness.
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* AI Outputs Are Advisory */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Eye className="h-5 w-5 text-[#42b1c2] mr-2" />
                  AI Outputs Are Advisory Only
                </h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800">
                    All AI-generated outputs on our platform are <strong>advisory and informational only</strong>. 
                    They should not be treated as definitive assessments or recommendations.
                  </p>
                </div>
                
                <p className="text-gray-600 mb-4">
                  This means:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Match suggestions indicate potential compatibility, not guaranteed fit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Profile insights are meant to assist, not replace, thorough human review</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Feedback (where provided) is general guidance, not professional evaluation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Users should exercise their own judgement when interpreting AI outputs</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Fairness & Bias */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Scale className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Fairness, Bias Monitoring & Transparency
                </h2>
                <p className="text-gray-600 mb-4">
                  We are committed to ensuring our AI systems are fair and do not discriminate against candidates:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Bias Prevention</h3>
                    <p className="text-sm text-gray-600">
                      We design our AI systems to focus on job-relevant factors such as qualifications, experience, 
                      and skills. We actively work to exclude protected characteristics (such as age, gender, 
                      ethnicity, or disability) from influencing AI outputs.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Ongoing Monitoring</h3>
                    <p className="text-sm text-gray-600">
                      We regularly review AI outputs for patterns that might indicate bias. If issues are identified, 
                      we take corrective action promptly and transparently.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Transparency Commitment</h3>
                    <p className="text-sm text-gray-600">
                      We clearly label AI-generated content and explain how AI is used in our processes. 
                      We do not hide AI involvement or present AI outputs as human-generated.
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Feedback Welcome</h3>
                    <p className="text-sm text-gray-600">
                      We encourage users to report concerns about AI fairness. Your feedback helps us improve 
                      our systems and ensure they serve all users equitably.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data & Privacy */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="h-5 w-5 text-[#42b1c2] mr-2" />
                  AI & Your Data
                </h2>
                <p className="text-gray-600 mb-4">
                  When using AI-assisted features:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Your personal data is processed in accordance with our Privacy Policy and GDPR principles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>AI processing is covered by the consent you provide when using our platform</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>You can request information about how AI has been used in relation to your profile</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Your data is not sold to third parties for AI training purposes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border-[#42b1c2]/30 bg-[#42b1c2]/5">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Mail className="h-5 w-5 text-[#42b1c2] mr-2" />
                  Questions About Our AI
                </h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about how we use AI, concerns about fairness, or want to report an issue:
                </p>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-gray-700"><strong>Email:</strong> info@lumamedglobal.com</p>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  We welcome feedback and are committed to continuous improvement in our AI practices.
                </p>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Future Developments</h2>
                <p className="text-gray-600 mb-4">
                  As AI technology evolves, we will continue to update this page to reflect changes in our AI usage. 
                  We are committed to:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Clearly communicating when new AI features are introduced</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Maintaining transparency about AI capabilities and limitations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Engaging with users and stakeholders on AI ethics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Following emerging best practices and regulatory guidance on AI in recruitment</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AITransparency;
